/* Cohort matrix view — iter84.
 * Cross-tab: students × 6 months post-activation.
 * Color-coded by delta vs baseline mean.
 * Aggregate row summarises the cohort's average trend. */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { ArrowClockwise, TrendUp, TrendDown, ChartBar, FilePdf } from '@phosphor-icons/react';

const cellColor = (delta) => {
  if (delta === null || delta === undefined) return '#F1F5F9';
  if (delta > 1.5) return '#059669';   // deep green
  if (delta > 0.5) return '#A7F3D0';   // light green
  if (delta > -0.3) return '#F1F5F9';  // gray (stable)
  if (delta > -1.5) return '#FECACA';  // light red
  return '#DC2626';                       // deep red
};
const cellText = (delta) => {
  if (delta === null || delta === undefined) return '#94A3B8';
  if (delta > 1.5) return '#FFFFFF';
  if (delta > -1.5) return '#0F172A';
  return '#FFFFFF';
};

export const SupportPlansCohortMatrix = ({ className, schoolYear }) => {
  const [monthsAhead, setMonthsAhead] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!schoolYear) return;
    setLoading(true);
    try {
      const params = { months_ahead: monthsAhead, school_year: schoolYear };
      if (className) params.class_name = className;
      const { data } = await api.get('/support-plans/matrix/cohort', { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement matrice');
    } finally { setLoading(false); }
  }, [monthsAhead, className, schoolYear]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <p className="text-center py-10 text-[#94A3B8]">Chargement…</p>;
  }
  if (!data || data.total_plans === 0) {
    return (
      <div className="text-center py-10 border-2 border-dashed border-[#E2E8F0] rounded-lg">
        <ChartBar size={40} className="mx-auto text-[#CBD5E1] mb-2" weight="duotone" />
        <p className="text-sm text-[#64748B]">
          Aucun plan actif ou terminé pour ce filtre. Activez au moins un plan
          pour voir la matrice de cohorte.
        </p>
      </div>
    );
  }

  const monthHeaders = Array.from({ length: monthsAhead }, (_, i) => i + 1);

  return (
    <div className="space-y-4" data-testid="sp-cohort-matrix">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs text-[#64748B]">Mois à suivre</Label>
          <Input type="number" min="1" max="24" value={monthsAhead}
            onChange={(e) => setMonthsAhead(Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 6)))}
            className="w-24 h-9" data-testid="sp-cohort-months-input" />
        </div>
        <Button variant="outline" onClick={load} data-testid="sp-cohort-refresh">
          <ArrowClockwise size={14} className="mr-1" /> Actualiser
        </Button>
        <Button variant="outline" onClick={() => {
          const params = new URLSearchParams({
            months_ahead: monthsAhead, school_year: schoolYear,
          });
          if (className) params.set('class_name', className);
          window.open(
            `${process.env.REACT_APP_BACKEND_URL}/api/support-plans/matrix/cohort/pdf?${params.toString()}`,
            '_blank',
          );
        }} data-testid="sp-cohort-pdf-btn">
          <FilePdf size={14} className="mr-1" /> Export PDF conseil
        </Button>
        <div className="flex gap-3 text-xs items-center ml-auto">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ background: '#059669' }}></span>
            Forte hausse (&gt; +1.5)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ background: '#A7F3D0' }}></span>
            Hausse (+0.5 à +1.5)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded border border-[#E2E8F0]" style={{ background: '#F1F5F9' }}></span>
            Stable
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ background: '#FECACA' }}></span>
            Baisse
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ background: '#DC2626' }}></span>
            Forte baisse
          </span>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="sp-cohort-table">
            <thead>
              <tr className="border-b border-[#E2E8F0]">
                <th className="text-left py-2 px-2 font-semibold text-[#64748B] sticky left-0 bg-white">
                  Élève
                </th>
                <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Classe</th>
                <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Activation</th>
                <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Baseline</th>
                {monthHeaders.map((m) => (
                  <th key={m} className="text-center py-2 px-2 font-semibold text-[#64748B]">
                    M+{m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.plan_id} className="border-b border-[#F1F5F9]"
                    data-testid={`sp-cohort-row-${r.plan_id}`}>
                  <td className="py-1.5 px-2 font-medium sticky left-0 bg-white">{r.student_name}</td>
                  <td className="py-1.5 px-2 text-center text-[#64748B]">{r.class_name}</td>
                  <td className="py-1.5 px-2 text-center text-[10px] text-[#64748B]">
                    {r.activation_date}
                  </td>
                  <td className="py-1.5 px-2 text-right font-semibold">
                    {r.baseline_mean != null ? `${r.baseline_mean}` : '—'}
                  </td>
                  {r.months.map((m) => (
                    <td key={m.index}
                        className="py-1 px-1 text-center font-semibold"
                        style={{
                          background: cellColor(m.delta),
                          color: cellText(m.delta),
                          minWidth: '52px',
                        }}
                        title={m.count ? `${m.count} note(s)` : 'Aucune note'}>
                      {m.mean != null
                        ? <>
                            <div className="text-xs">{m.mean}</div>
                            {m.delta != null && (
                              <div className="text-[9px] opacity-80">
                                {m.delta > 0 ? '+' : ''}{m.delta}
                              </div>
                            )}
                          </>
                        : <span className="text-[10px]">—</span>
                      }
                    </td>
                  ))}
                </tr>
              ))}
              {/* Aggregate row */}
              <tr className="bg-[#EEF2FF] border-t-2 border-[#0052FF]">
                <td className="py-2 px-2 font-bold text-[#0052FF] sticky left-0 bg-[#EEF2FF]">
                  ⌀ Cohorte
                </td>
                <td colSpan={3} className="py-2 px-2 text-[#64748B] text-[10px]">
                  Sur {data.total_plans} plan(s) — % amélioré / % dégradé par mois
                </td>
                {monthHeaders.map((m) => {
                  const agg = data.aggregate.find((a) => a.index === m);
                  if (!agg) return <td key={m}></td>;
                  const pctImp = agg.count ? Math.round(agg.improved * 100 / agg.count) : 0;
                  const pctDeg = agg.count ? Math.round(agg.degraded * 100 / agg.count) : 0;
                  return (
                    <td key={m} className="py-1.5 px-1 text-center">
                      <div className="text-xs font-bold text-[#0052FF]">
                        {agg.avg_mean != null ? agg.avg_mean : '—'}
                      </div>
                      <div className="text-[9px] flex items-center justify-center gap-1">
                        <span className="text-emerald-600 flex items-center">
                          <TrendUp size={8} weight="fill" />{pctImp}%
                        </span>
                        <span className="text-red-600 flex items-center">
                          <TrendDown size={8} weight="fill" />{pctDeg}%
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
